<template>
  <div class="custom-dropdown" :class="{ open: open }" ref="dropdown">
    <div class="dropdown-selected" @click="toggleDropdown">
      <div class="selected-content">
        <img
          v-if="shouldShowAvatars && selectedOption && selectedOption.avatar"
          :src="selectedOption.avatar"
          :alt="selectedOption.label"
          class="dropdown-avatar"
          @error="handleImageError"
        />
        <div
          v-if="shouldShowAvatars && selectedOption && !selectedOption.avatar"
          class="dropdown-avatar-initials"
        >
          {{ getInitials(selectedOption.label) }}
        </div>
        <span>{{ selectedLabel }}</span>
      </div>
      <ChevronDown :stroke-width="1" class="dropdown-arrow" />
    </div>
    <div v-if="open" class="dropdown-options">
      <div
        v-for="option in options"
        :key="option.value"
        class="dropdown-option"
        :class="{ selected: option.value === modelValue }"
        @click="selectOption(option.value)"
      >
        <img
          v-if="shouldShowAvatars && option.avatar"
          :src="option.avatar"
          :alt="option.label"
          class="dropdown-avatar-small"
          @error="handleImageError"
        />
        <div
          v-if="shouldShowAvatars && !option.avatar"
          class="dropdown-avatar-initials-small"
        >
          {{ getInitials(option.label) }}
        </div>
        <span>{{ option.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ChevronDown } from "lucide-vue-next";
export default {
  name: "CustomDropdown",
  components: { ChevronDown },
  props: {
    modelValue: [String, Number],
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Selecteer...",
    },
    hideAvatar: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      open: false,
    };
  },
  watch: {
    options: {
      deep: true,
      handler() {
        // No-op handler to satisfy Vue's watcher requirements
      },
    },
  },
  computed: {
    selectedLabel() {
      const found = this.options.find((o) => o.value === this.modelValue);
      return found ? found.label : this.placeholder;
    },
    selectedOption() {
      return this.options.find((o) => o.value === this.modelValue);
    },
    shouldShowAvatars() {
      return !this.hideAvatar;
    },
  },
  methods: {
    getInitials(label) {
      if (!label) return "?";
      const parts = label.split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return label.substring(0, 2).toUpperCase();
    },
    handleImageError() {
      // Image failed to load, initials will be shown instead
    },
    toggleDropdown() {
      this.open = !this.open;
    },
    selectOption(value) {
      this.$emit("update:modelValue", value);
      this.open = false;
    },
    handleClickOutside(event) {
      if (!this.$refs.dropdown.contains(event.target)) {
        this.open = false;
      }
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  },
};
</script>

<style scoped>
/* --- Styling aligned with Contacts.vue custom dropdowns --- */
.custom-dropdown {
  position: relative;
}

.dropdown-selected {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  height: 2.5rem;
  font-size: 0.97rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 120px;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: 0.75rem;
}

.selected-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-selected:hover,
.dropdown-selected:focus {
  border-color: var(--primary_color, #4f46e5);
}

.dropdown-arrow {
  margin-left: 0.5em;
  color: var(--text-primary, #000);
  flex-shrink: 0;
}

.dropdown-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid var(--border);
}

.dropdown-avatar-initials {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #00ff00;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.dropdown-options {
  position: absolute;
  top: 110%;
  left: 0;
  min-width: 120px;
  width: 100%;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  padding: 0.15rem 0;
  max-height: 400px;
  overflow-y: auto;
}

.dropdown-option {
  color: var(--text-primary);
  padding: 0.5rem 0.7rem;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dropdown-option:hover {
  background: var(--primary_color_alpha_10);
  color: var(--primary_color);
}

.dropdown-option.selected {
  background: var(--primary_color_alpha_10);
  color: var(--primary_color);
  font-weight: 500;
}

.dropdown-avatar-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid var(--border);
}

.dropdown-avatar-initials-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00ff00;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 600;
  flex-shrink: 0;
}
</style>
